import React, { useEffect, useRef, useCallback, useState } from 'react';
import { AnimatePresence } from 'motion/react';
import { useChatStore } from '@/stores/chatStore';
import { checkActiveChat, getMessages } from '@/services/chatService';
import { ChatButton } from './ChatButton';
import { ChatWindow } from './ChatWindow';

// ─── Polling Intervals ────────────────────────────────────────
const MESSAGE_POLL_MS = 3_000; // 3s when active + open
const STATUS_POLL_MS = 15_000; // 15s when pending

export const ChatWidget: React.FC = () => {
  const isOpen = useChatStore((s) => s.isOpen);
  const room = useChatStore((s) => s.room);
  const setRoom = useChatStore((s) => s.setRoom);
  const mergeMessages = useChatStore((s) => s.mergeMessages);
  const setMessages = useChatStore((s) => s.setMessages);
  const setLoading = useChatStore((s) => s.setLoading);
  const setAdminTyping = useChatStore((s) => s.setAdminTyping);
  const clearSession = useChatStore((s) => s.clearSession);

  const messagesPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initRef = useRef(false);

  // ─── Session Initialization (on mount) ───────────────────────
  // Always probe the backend for an active session so a room that was
  // started in another tab (or before the localStorage `domainboost-chat`
  // entry was persisted) is still picked up. Previously this short-circuited
  // when there was no persisted room and silently dropped any live session.
  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const init = async () => {
      const storedRoom = useChatStore.getState().room;
      // Only show the full-screen loader when we already think we have a
      // room — otherwise the idle/start screen is the correct UX.
      if (storedRoom) setLoading(true);
      try {
        const result = await checkActiveChat();
        if (result.has_active_chat && result.room) {
          setRoom(result.room);
          // Backend returns messages ordered DESC (newest first). The UI
          // renders oldest-first (top → bottom), so reverse before storing.
          const msgResult = await getMessages(result.room.id);
          setMessages([...msgResult.messages].reverse());
        } else if (storedRoom) {
          // We had a persisted room but the backend says it's gone —
          // clear stale state. Don't clear if we never had one (avoids
          // wiping unreadCount unnecessarily).
          clearSession();
        }
      } catch {
        // If the check fails AND we had a stale persisted room, clear it
        // so we don't keep trying to poll a dead session forever.
        if (storedRoom) clearSession();
      } finally {
        setLoading(false);
      }
    };

    void init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Message Polling (active room) ───────────────────────────
  // Polls even when the widget is collapsed so the unread badge can update
  // when the admin replies while the user has the panel minimized.
  // mergeMessages handles dedupe + reorder + unread bookkeeping itself.
  const pollMessages = useCallback(async () => {
    const currentRoom = useChatStore.getState().room;
    if (currentRoom?.status !== 'active') return;

    try {
      const result = await getMessages(currentRoom.id);
      // Backend orders DESC (newest first); UI renders ASC. Reverse before
      // merging so admin replies don't render above the user's prior turn.
      mergeMessages([...result.messages].reverse());
    } catch {
      // Silently fail — next poll will retry
    }
  }, [mergeMessages]);

  useEffect(() => {
    if (messagesPollRef.current) {
      clearInterval(messagesPollRef.current);
      messagesPollRef.current = null;
    }

    if (room?.status === 'active') {
      // Fire once immediately so a freshly-assigned room doesn't have to
      // wait MESSAGE_POLL_MS before the user sees the first admin message.
      void pollMessages();
      messagesPollRef.current = setInterval(() => {
        void pollMessages();
      }, MESSAGE_POLL_MS);
    }

    return () => {
      if (messagesPollRef.current) {
        clearInterval(messagesPollRef.current);
        messagesPollRef.current = null;
      }
    };
  }, [room?.status, pollMessages]);

  // ─── Status Polling (pending room) ───────────────────────────
  const pollStatus = useCallback(async () => {
    const currentRoom = useChatStore.getState().room;
    if (currentRoom?.status !== 'pending') return;

    try {
      const result = await checkActiveChat();
      if (result.has_active_chat && result.room) {
        setRoom(result.room);

        // If room just became active, fetch messages (reverse for ASC order).
        // The active-room poll effect will take over from here.
        if (result.room.status === 'active') {
          const msgResult = await getMessages(result.room.id);
          setMessages([...msgResult.messages].reverse());
        }
      } else {
        // Room was closed externally
        clearSession();
      }
    } catch {
      // Silently fail — next poll will retry
    }
  }, [setRoom, setMessages, clearSession]);

  useEffect(() => {
    if (statusPollRef.current) {
      clearInterval(statusPollRef.current);
      statusPollRef.current = null;
    }

    if (room?.status === 'pending') {
      // Run once immediately so the assignment is picked up faster than
      // STATUS_POLL_MS would allow on its own.
      void pollStatus();
      statusPollRef.current = setInterval(() => {
        void pollStatus();
      }, STATUS_POLL_MS);
    }

    return () => {
      if (statusPollRef.current) {
        clearInterval(statusPollRef.current);
        statusPollRef.current = null;
      }
    };
  }, [room?.status, pollStatus]);

  // ─── Clear admin typing after inactivity ─────────────────────
  const typingClearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isAdminTyping = useChatStore((s) => s.isAdminTyping);

  useEffect(() => {
    if (isAdminTyping) {
      typingClearRef.current = setTimeout(() => {
        setAdminTyping(false);
      }, 4_000);
    }

    return () => {
      if (typingClearRef.current) {
        clearTimeout(typingClearRef.current);
        typingClearRef.current = null;
      }
    };
  }, [isAdminTyping, setAdminTyping]);

  // ─── Compensate for scrollbar removal when dialogs lock scroll ──
  const [scrollBarSize, setScrollBarSize] = useState(0);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const locked = document.body.hasAttribute('data-scroll-locked');
      if (locked) {
        const size = getComputedStyle(document.body).getPropertyValue(
          '--removed-body-scroll-bar-size'
        );
        setScrollBarSize(size ? parseInt(size, 10) || 0 : 0);
      } else {
        setScrollBarSize(0);
      }
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['data-scroll-locked'] });
    return () => observer.disconnect();
  }, []);

  // ─── Render ──────────────────────────────────────────────────
  // Floating chat — unified position across PE / admin / webapp:
  // bottom 24px, right 24px, z-50. `scrollBarSize` keeps the button from
  // shifting when Radix portals lock body scroll (modals open).
  return (
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        right: `${24 + scrollBarSize}px`,
        zIndex: 50,
      }}
    >
      <AnimatePresence mode="wait">
        {isOpen ? <ChatWindow key="chat-window" /> : <ChatButton key="chat-button" />}
      </AnimatePresence>
    </div>
  );
};
